/* eslint no-restricted-imports: 0 */
import { useRef } from "react"
import {
    AriaCalendarGridProps,
    DateValue,
    RangeCalendarProps,
    mergeProps,
    useCalendarCell,
    useCalendarGrid,
    useFocusRing,
    useLocale,
    useRangeCalendar,
} from "react-aria"
import {
    CalendarDate,
    DateDuration,
    endOfMonth,
    getLocalTimeZone,
    getWeeksInMonth,
    isSameDay,
    isSameMonth,
} from "@internationalized/date"
import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from "@heroicons/react/20/solid"
import { classNames } from "../../../utils/classNames"
import { getWeekNumber, monthNamesArray, weekdayNamesMap } from "./utils"
import { plural } from "@figurepos/fgr-lib-business-logic/lib/formatters/plural"
import { differenceInDays, isToday, parse } from "date-fns"
import { ISO_DATE_FORMAT, weekStartDayToCalendarConfig } from "../DateRangePicker/utils"
import { WeekStartDay } from "../../screens/settings/types"
import { RangeCalendarState } from "react-stately"

export const ReactAriaRangeCalendarBase = ({
    rangeCalendarProps,
    rangeCalendarState,
    weekStartDay,
}: {
    rangeCalendarProps: RangeCalendarProps<DateValue>
    rangeCalendarState: RangeCalendarState
    weekStartDay: WeekStartDay
}) => {
    const ref = useRef(null)
    const state = rangeCalendarState

    const { calendarProps } = useRangeCalendar(rangeCalendarProps, state, ref)

    const startMonthLabel = monthNamesArray[state.visibleRange.start.month - 1]
    const startYearLabel = state.visibleRange.start.year

    const endMonthLabel = monthNamesArray[state.visibleRange.end.month - 1]
    const endYearLabel = state.visibleRange.end.year

    const setMonthBack = () => {
        state.focusPreviousPage()
    }

    const setMonthForward = () => {
        state.focusNextPage()
    }

    const setYearBack = () => {
        // There is bug, when year is changed back or forward, it also changes one month back or forward
        // I created bug-ticket here so it might get resolved in the future:
        // https://github.com/adobe/react-spectrum/issues/7645
        state.focusPreviousSection(true)
    }

    const setYearForward = () => {
        state.focusNextSection(true)
    }

    const isNextMonthDisabled = state.maxValue ? state.visibleRange.end.compare(state.maxValue) >= 0 : false
    const isNextYearDisabled = state.maxValue ? state.visibleRange.end.year >= state.maxValue.year : false

    const isPrevMonthDisabled = state.minValue ? state.visibleRange.start.compare(state.minValue) <= 0 : false
    const isPrevYearDisabled = state.minValue ? state.visibleRange.start.year <= state.minValue.year : false

    return (
        <div {...calendarProps} ref={ref} className={calendarClasses.wrap}>
            <div>
                <div className={calendarClasses.headerWrap}>
                    <div className={calendarClasses.arrowsLeftWrap}>
                        <button
                            type="button"
                            onClick={setYearBack}
                            disabled={isPrevYearDisabled}
                            className={classNames(
                                calendarClasses.arrowBtn,
                                isPrevYearDisabled && calendarClasses.arrowBtnDisabled
                            )}>
                            <ChevronDoubleLeftIcon className={calendarClasses.arrowIcon} />
                        </button>
                        <button
                            type="button"
                            onClick={setMonthBack}
                            disabled={isPrevMonthDisabled}
                            className={classNames(
                                calendarClasses.arrowBtn,
                                isPrevMonthDisabled && calendarClasses.arrowBtnDisabled
                            )}>
                            <ChevronLeftIcon className={calendarClasses.arrowIcon} />
                        </button>
                    </div>
                    <div className={calendarClasses.headerTitle}>
                        {startMonthLabel} {startYearLabel}
                    </div>
                </div>
                <div className={calendarClasses.firstCalendarWrap}>
                    <CalendarGrid state={state} weekStartDay={weekStartDay} />
                </div>
            </div>
            <div>
                <div className={calendarClasses.headerWrap}>
                    <div className={calendarClasses.arrowsRightWrap}>
                        <button
                            type="button"
                            onClick={setMonthForward}
                            disabled={isNextMonthDisabled}
                            className={classNames(
                                calendarClasses.arrowBtn,
                                isNextMonthDisabled && calendarClasses.arrowBtnDisabled
                            )}>
                            <ChevronRightIcon className={calendarClasses.arrowIcon} />
                        </button>
                        <button
                            type="button"
                            onClick={setYearForward}
                            disabled={isNextYearDisabled}
                            className={classNames(
                                calendarClasses.arrowBtn,
                                isNextYearDisabled && calendarClasses.arrowBtnDisabled
                            )}>
                            <ChevronDoubleRightIcon className={calendarClasses.arrowIcon} />
                        </button>
                    </div>
                    <div className={calendarClasses.headerTitle}>
                        {endMonthLabel} {endYearLabel}
                    </div>
                </div>
                <div className={calendarClasses.secondCalendarWrap}>
                    <CalendarGrid state={state} offset={{ months: 1 }} weekStartDay={weekStartDay} />
                </div>
            </div>
        </div>
    )
}

const calendarClasses = {
    wrap: "inline-flex",
    arrowsLeftWrap: "absolute left-0 flex",
    arrowsRightWrap: "absolute right-0 flex",
    arrowBtn:
        "flex items-center justify-center bg-transparent m-0 p-0 w-[27px] h-[27px] cursor-pointer text-primary-color leading-none hover:bg-primary-color/10 rounded-full",
    arrowBtnDisabled: "text-black/25 pointer-events-none",
    arrowIcon: "w-[20px] h-auto",
    headerWrap: "relative border-b border-gray-200 pb-2 mb-3 flex items-center justify-center",
    headerTitle: "text-center font-bold text-[16px] text-black/90",
    firstCalendarWrap: "mr-3",
    secondCalendarWrap: "ml-3",
}

const CalendarGrid = ({
    state,
    offset,
    weekStartDay,
    ...props
}: { state: RangeCalendarState; offset?: DateDuration; weekStartDay: WeekStartDay } & AriaCalendarGridProps) => {
    const monthStartDate = offset ? state.visibleRange.start.add(offset) : state.visibleRange.start
    const monthEndDate = endOfMonth(monthStartDate as CalendarDate)

    const calendarWeekStartDay = weekStartDayToCalendarConfig(weekStartDay)
    const { gridProps, headerProps, weekDays } = useCalendarGrid(
        {
            ...props,
            firstDayOfWeek: calendarWeekStartDay,
            weekdayStyle: "long",
            startDate: monthStartDate,
            endDate: monthEndDate,
        },
        state
    )

    // Get the number of weeks in the month so we can render the proper number of rows.
    const { locale } = useLocale()
    const weeksInMonth = getWeeksInMonth(monthStartDate, locale, calendarWeekStartDay)

    return (
        <div className={gridClasses.wrap}>
            <div>
                <WeekNumbers
                    state={state}
                    weeksInMonth={weeksInMonth}
                    monthStartDate={monthStartDate}
                    weekStartDay={weekStartDay}
                />
            </div>
            <table {...gridProps} cellPadding="0">
                <thead {...headerProps}>
                    <tr>
                        {weekDays.map((day, index) => (
                            <th className={gridClasses.weekDayLabelWrap} key={index}>
                                {weekdayNamesMap[day]}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {new Array(weeksInMonth).fill(0).map((_, weekIndex) => (
                        <tr key={weekIndex}>
                            {state
                                .getDatesInWeek(weekIndex, monthStartDate)
                                .map((date, i) =>
                                    date ? (
                                        <CalendarCell key={i} state={state} date={date} currentMonth={monthStartDate} />
                                    ) : (
                                        <td key={i} />
                                    )
                                )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

const gridClasses = {
    wrap: "flex",
    weekDayLabelWrap: "text-black/70 text-center font-bold",
}

const CalendarCell = ({
    state,
    date,
    currentMonth,
}: {
    state: RangeCalendarState
    date: CalendarDate
    currentMonth: CalendarDate
}) => {
    const ref = useRef(null)

    const { cellProps, buttonProps, isSelected, isDisabled, formattedDate } = useCalendarCell(
        { date: date },
        state,
        ref
    )

    // The start and end date of the selected range will have
    // an emphasized appearance.
    const isSelectionStart = state.highlightedRange ? isSameDay(date, state.highlightedRange.start) : isSelected
    const isSelectionEnd = state.highlightedRange ? isSameDay(date, state.highlightedRange.end) : isSelected

    const { focusProps, isFocusVisible } = useFocusRing()

    const isOutsideMonth = !isSameMonth(currentMonth, date)

    const getDifferenceInDays = () => {
        const { highlightedRange } = state
        if (!highlightedRange || !highlightedRange.start || !highlightedRange.end) {
            return 0
        }
        const { start, end } = highlightedRange
        return differenceInDays(end.toDate(getLocalTimeZone()), start.toDate(getLocalTimeZone())) + 1
    }

    const totalSelectedDays = getDifferenceInDays()
    const showTotalDaysTooltip = (isSelectionEnd || isSelectionStart) && totalSelectedDays > 0

    const today = isToday(parse(date.toString(), ISO_DATE_FORMAT, new Date()))

    return (
        <td {...cellProps} className={classNames(cellClasses.wrap, isFocusVisible ? "z-10" : "z-0")}>
            <div
                {...mergeProps(buttonProps, focusProps)}
                ref={ref}
                hidden={isOutsideMonth}
                // Disable controlling the calendar with keyboard, it was causing some bugs with "Enter" and "Tab" keys pressed
                tabIndex={undefined}
                className={classNames(
                    cellClasses.cell,
                    isDisabled ? cellClasses.cellDisabled : null,
                    isSelected ? cellClasses.selected : null,
                    isSelectionStart || isSelectionEnd ? cellClasses.selectedStartEnd : null,
                    isSelectionStart ? cellClasses.selectedStart : null,
                    isSelectionEnd ? cellClasses.selectedEnd : null,
                    today ? cellClasses.today : null
                )}>
                {!isDisabled ? (
                    <div
                        className={classNames(
                            cellClasses.cellHover,
                            isFocusVisible ? cellClasses.cellHoverOnFocus : null
                        )}
                    />
                ) : null}
                {showTotalDaysTooltip ? (
                    <div className={cellClasses.selectedDaysTooltip}>
                        {totalSelectedDays} {plural("Day", totalSelectedDays)}
                        <div className={cellClasses.tooltipArrow} />
                    </div>
                ) : null}
                {formattedDate}
            </div>
        </td>
    )
}

const cellClasses = {
    wrap: "relative py-[1px]",
    cell: "group relative w-[34px] h-[38px] text-[14px] outline-none leading-none flex items-center justify-center cursor-pointer",
    cellDisabled: "text-black/25 bg-gray-100/70 pointer-events-none cursor-default",
    selected: "bg-primary-color/20",
    selectedStartEnd: "font-bold bg-primary-color text-white hover:bg-primary-color",
    selectedStart: " rounded-l-[4px]",
    selectedEnd: "rounded-r-[4px]",
    today: "font-bold underline",
    cellHover: "hidden group-hover:block absolute rounded-full w-[30px] h-[30px] border border-primary-color",
    cellHoverOnFocus: "block",
    selectedDaysTooltip:
        "hidden group-hover:block absolute top-[-25px] bg-black/75 text-center text-white font-normal w-[64px] rounded-[4px] text-[14px] py-[4px]",
    tooltipArrow:
        "absolute h-0 w-0 left-[30px] bottom-[-4px] border-x-[3px] border-x-transparent border-t-[4px] border-t-black/75",
}

const WeekNumbers = ({
    state,
    monthStartDate,
    weeksInMonth,
    weekStartDay,
}: {
    state: RangeCalendarState
    monthStartDate: CalendarDate
    weeksInMonth: number
    weekStartDay: WeekStartDay
}) => {
    const handleOnClick = (weekIndex: number, weekStartDate: DateValue | null, weekEndDate: DateValue | null) => {
        if (!weekStartDate || !weekEndDate) {
            return
        }

        // When selecting week-number, clear the anchor date if it's set.
        state.setAnchorDate(null)

        let startDate = weekStartDate
        let endDate = weekEndDate

        // If min or max date is set, we need to make sure that the selected range is within the limits.
        const minDate = state.minValue
        const maxDate = state.maxValue

        if ((maxDate && startDate.compare(maxDate) > 0) || (minDate && endDate.compare(minDate) < 0)) {
            return
        }
        if (minDate && startDate.compare(minDate) < 0) {
            startDate = minDate
        }
        if (maxDate && endDate.compare(maxDate) > 0) {
            endDate = maxDate
        }

        state.setValue({ start: startDate, end: endDate })

        // When selecting week-number, if start or end date is outside of visible range,
        // we need to focus previous or next page to make sure that the selected range is visible.
        const visibleRangeStart = state.visibleRange.start
        const visibleRangeEnd = state.visibleRange.end

        if (startDate && visibleRangeStart.compare(startDate) > 0) {
            state.focusPreviousPage()
        }
        if (endDate && visibleRangeEnd.compare(endDate) < 0) {
            state.focusNextPage()
        }
    }

    const isWeekSelected = (weekStartDate: string | null, weekEndDate: string | null): boolean => {
        const startDate = state.value?.start?.toString()
        const endDate = state.value?.end?.toString()

        if (!startDate || !endDate || !weekStartDate || !weekEndDate) {
            return false
        }

        return startDate === weekStartDate && endDate === weekEndDate
    }

    return (
        <div>
            <div className={classNames(weekNumberClasses.weekNumberTitle)}>Wk</div>
            {new Array(weeksInMonth).fill(0).map((_, weekIndex) => {
                const weekNumber = getWeekNumber(
                    monthStartDate.year,
                    monthStartDate.month - 1,
                    weekIndex * 7 + 1,
                    weekStartDay
                )

                const weekStartDate = state.getDatesInWeek(weekIndex, monthStartDate)[0] as DateValue | null
                const weekEndDate = state.getDatesInWeek(weekIndex, monthStartDate)[6] as DateValue | null

                const weekSelected = isWeekSelected(weekStartDate?.toString() ?? null, weekEndDate?.toString() ?? null)

                return (
                    <div key={weekIndex} className={weekNumberClasses.weekNumberCellWrap}>
                        <button
                            type="button"
                            className={classNames(
                                weekNumberClasses.weekNumberCell,
                                weekSelected ? weekNumberClasses.weekNumberCellSelected : null
                            )}
                            onClick={() => handleOnClick(weekIndex, weekStartDate, weekEndDate)}>
                            {weekNumber}
                        </button>
                        <div className={weekNumberClasses.weekNumberHoverWrap}>
                            <div className={weekNumberClasses.weekNumberHover} />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

const weekNumberClasses = {
    weekNumberTitle: "text-center font-bold text-[#1677FF] opacity-70",
    weekNumberCellWrap: "py-[1px] relative group",
    weekNumberCell:
        "w-[34px] h-[38px] outline-none leading-none flex items-center justify-center cursor-pointer bg-transparent text-[13px] text-[#1677FF] opacity-70 group-hover:opacity-100",
    weekNumberCellSelected: "opacity-100 underline",
    weekNumberHoverWrap:
        "hidden group-hover:flex inset-0 absolute flex-col justify-center items-center pointer-events-none",
    weekNumberHover: "rounded-full w-[30px] h-[30px] border border-[#1677FF]",
}
